import { useNavigate } from '@tanstack/react-location';
import React, { useState } from 'react';
import useAuthentication from '../../../api/use-authentication';
import { useFlexAssessmentContext } from '../../../context/context';
import buttons from '../../../css/buttons.module.scss';
import styles from '../../../css/modal.module.scss';
import { FlexAssessmentContextAction } from '../../../types/flex-assessment-context-action';
import { NotificationType } from '../../../types/notification-type';
import { RegistrationFieldName } from '../../../types/registration-field-name';
import { Field } from '../../../types/types';
import { MIXPANEL_EVENTS } from '../../../utils/mixpanel-consts';
import { track } from '../../../utils/mixpanel-helper';
import { STRINGS } from '../../../utils/strings';
import { validateEmail } from '../../../utils/validation';
import Input from '../../form-fields/input';
import Spinner, { SPINNER_COLORS } from '../../spinner';
import { HASH_URLS } from '../auth-modal';

const EMAIL_FIELD_NAME = 'email';

export const RequestVerificationCodeForm = React.memo((): JSX.Element => {
  const navigate = useNavigate();
  const { state, dispatch } = useFlexAssessmentContext();
  const email: Field = state.email;
  const isValid = email.isValid;
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const { requestPasswordVerificationCode } = useAuthentication();

  const onRequestVerificationCode = async (
    event: React.FormEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    track(MIXPANEL_EVENTS.BUTTON_CLICK, {
      button: 'request-verification-code',
    });
    if (!isValid || !email.value) {
      return;
    }
    setIsSubmittingForm(true);
    const req = await requestPasswordVerificationCode(email.value as string);
    setIsSubmittingForm(false);
    if (req === 'Email Sent') {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: STRINGS.SUCCESS.PASSWORD_RESET_EMAIL_SENT,
          type: NotificationType.Success,
        },
      });
      setTimeout(() => {
        dispatch({ type: FlexAssessmentContextAction.dismissNotification });
        navigate({ to: `#${HASH_URLS.CHOOSE_PASSWORD}` });
      }, 4000);
    }
  };

  return (
    <form className={styles.modalContent}>
      <h3 className={styles.modalTitle}>Reset your password</h3>
      <p className={styles.p}>
        Type your email below so that we can send you a verification code.
      </p>
      <Input
        type="email"
        inputMode="email"
        value={email.value as string}
        name={EMAIL_FIELD_NAME}
        placeholder="Your Email"
        validator={(value: string): boolean => validateEmail(value)}
        errorString={STRINGS.ERRORS.EMAIL_NOT_VALID}
        callback={(
          name: string,
          value: string,
          isValid: boolean | undefined
        ): void => {
          dispatch({
            type: FlexAssessmentContextAction.setFieldValue,
            field: RegistrationFieldName.email,
            value,
            isValid,
          });
        }}
      />
      <button
        onClick={onRequestVerificationCode}
        className={buttons.submitBtn}
        disabled={!isValid || isSubmittingForm}
      >
        {isSubmittingForm && <Spinner color={SPINNER_COLORS.LIGHT} />}
        Send Code
      </button>
    </form>
  );
});
